<div *ngIf="loading || loadFailed" class="loading-container">
  <app-loader variant="almostready" [failed]="loadFailed" (retry)="loadExistingReport()"></app-loader>
</div>

<!-- B7: report generation failed. Her answers are still in the form behind this,
     so Retry re-runs the same save. -->
<div *ngIf="!loading && !loadFailed && generateFailed" class="loading-container">
  <app-loader variant="almostready" [failed]="true" (retry)="generateReport()"></app-loader>
</div>

<div *ngIf="!loading && !loadFailed && generating" class="report-generating">
  <div class="generating-inner">
    <div class="status-text">
      {{ 'doctorReport.generating.status' | translate }}
    </div>

    <!-- The fill line she watches until the report is ready. It sits UNDER the
         status text on purpose: at >=600px the shell nav is a fixed TOP bar
         (z-index 9000) and a line at the very top of this overlay was hidden
         behind it. -->
    <div
      class="progress-wrapper"
      role="progressbar"
      aria-valuemin="0"
      aria-valuemax="100"
      [attr.aria-valuenow]="generationProgressRounded"
      [attr.aria-label]="'doctorReport.generating.status' | translate"
    >
      <div class="progress-bar" [style.width.%]="generationProgress"></div>
    </div>

    <div class="insight-card" *ngIf="generationTipKey">
      <span class="card-header">
        {{ 'wellnessTip.header' | translate }}
      </span>
      <p class="tip-text">{{ generationTipTextKey | translate }}</p>
    </div>

    <div class="action-container">
      <button
        type="button"
        class="continue-btn"
        [class.visible]="generationReady"
        [disabled]="!generationReady"
        (click)="viewGeneratedReport()"
      >
        {{ 'doctorReport.generating.ready' | translate }}
      </button>
    </div>

    <div class="disclaimer-box">
      <p class="disclaimer-text">
        {{ 'wellnessTip.disclaimer' | translate }}
        <span class="emergency-note">
          {{ 'wellnessTip.emergency' | translate }}
        </span>
      </p>
    </div>
  </div>
</div>

<div *ngIf="!loading && !loadFailed && !generating && !generateFailed" class="report-builder">
  <!-- Top bar -->
  <div class="topbar">
    <div class="top-row">
      <button class="close-btn" (click)="goBack()">&times;</button>
      <div class="autosave-status" *ngIf="autoSaveStatus !== 'idle'">
        <span *ngIf="autoSaveStatus === 'saving'" class="saving">{{ 'doctorReport.autosave.saving' | translate }}</span>
        <span *ngIf="autoSaveStatus === 'saved'">&#10003; {{ 'doctorReport.autosave.saved' | translate }}</span>
        <span *ngIf="autoSaveStatus === 'error'" class="error">{{ 'doctorReport.autosave.error' | translate }}</span>
      </div>
    </div>
  </div>

  <div class="page-header">
    <h1>{{ 'doctorReport.pageTitle' | translate }}</h1>
    <p>{{ 'doctorReport.pageSubtitle' | translate }}</p>
  </div>

  <main class="content">
    <!-- Step 1: Clinical Context — Medications & Supplements -->
    <section class="card">
      <span class="small-label">{{ 'doctorReport.step1Label' | translate }}</span>
      <h2>{{ 'doctorReport.medsAndSupplements.title' | translate }}</h2>

      <div class="explanation-box">
        <p>
          <strong>{{ 'doctorReport.medsAndSupplements.whyTitle' | translate }}</strong>
          {{ 'doctorReport.medsAndSupplements.whyText' | translate }}
        </p>
        <!-- Said before she starts adding, because the pill is the one people
             leave out of a medication list. -->
        <p class="pills-note">{{ 'doctorReport.medsAndSupplements.pillsNote' | translate }}</p>
      </div>

      <div class="entries-container">
        <!-- Saved medication summary cards -->
        <ng-container *ngFor="let m of medications; let i = index">
          <div *ngIf="editingMedicationIndex !== i && m.name" class="summary-card flexRowParent">
            <div class="flexItemBiggest summary-info">
              <div class="summary-title" [title]="m.name">{{ m.name }}</div>
              <div class="summary-meta">{{ getMedicationSummary(m) }}</div>
            </div>
            <div class="flexItem summary-actions">
              <a class="edit-link" (click)="toggleEditMedication(i)">{{ 'doctorReport.actions.edit' | translate }}</a>
              <a class="remove-link" (click)="removeMedication(i)">{{ 'doctorReport.actions.remove' | translate }}</a>
            </div>
          </div>

          <!-- Expanded edit form -->
          <div *ngIf="editingMedicationIndex === i" class="med-item-form medication-entry">
            <div class="field-group">
              <label class="label">{{ 'doctorReport.medsAndSupplements.nameLabel' | translate }}</label>
              <div class="autocomplete-wrapper">
                <input class="input" [(ngModel)]="m.name"
                  [placeholder]="'doctorReport.medsAndSupplements.namePlaceholder' | translate"
                  (input)="onMedicationInput(i, m.name)" (ngModelChange)="triggerAutoSave()" autocomplete="off" />
                <div class="autocomplete-dropdown" *ngIf="isAutocompleteOpen(i)">
                  <div class="autocomplete-item" *ngFor="let med of filteredMedications"
                    (mousedown)="selectMedication(i, med)">
                    {{ med }}
                  </div>
                </div>
              </div>
            </div>

            <div class="grid-2">
              <div class="field-group">
                <label class="label">{{ 'doctorReport.medsAndSupplements.doseLabel' | translate }}
                  <span class="optional">{{ 'doctorReport.optional' | translate }}</span>
                </label>
                <input class="input" [(ngModel)]="m.dose"
                  [placeholder]="'doctorReport.medsAndSupplements.dosePlaceholder' | translate" />
              </div>
              <div class="field-group">
                <label class="label">{{ 'doctorReport.medsAndSupplements.frequencyLabel' | translate }}</label>
                <select class="select" [attr.aria-label]="'doctorReport.medsAndSupplements.frequencyLabel' | translate" [(ngModel)]="m.frequency" (ngModelChange)="triggerAutoSave()">
                  <option *ngFor="let f of frequencyOptions" [value]="f">
                    {{ 'doctorReport.frequency.' + f | translate }}
                  </option>
                </select>
              </div>
            </div>

            <div class="field-group" *ngIf="m.frequency === 'other'">
              <label class="label">{{ 'doctorReport.frequency.customLabel' | translate }}</label>
              <input class="input" [(ngModel)]="m.frequencyCustom"
                [placeholder]="'doctorReport.frequency.customPlaceholder' | translate"
                (ngModelChange)="triggerAutoSave()" />
            </div>

            <div class="field-group">
              <label class="label">{{ 'doctorReport.medsAndSupplements.consistencyLabel' | translate }}</label>
              <div class="option-list">
                <div *ngFor="let c of consistencyOptions" class="option-row"
                  [class.selected]="m.consistency === c" (click)="selectConsistency(i, c)">
                  <span>{{ 'doctorReport.consistency.' + c | translate }}</span>
                  <div class="radio-circle"></div>
                </div>
              </div>
            </div>

            <div class="field-group">
              <label class="label">{{ 'doctorReport.medsAndSupplements.startLabel' | translate }}</label>
              <select class="select" [attr.aria-label]="'doctorReport.medsAndSupplements.startLabel' | translate" [(ngModel)]="m.approximateStart" (ngModelChange)="triggerAutoSave()">
                <option value="">—</option>
                <option *ngFor="let opt of startDateOptions" [value]="opt">
                  {{ 'doctorReport.treatment.startOption_' + opt | translate }}
                </option>
              </select>
            </div>

            <div class="field-group datepicker-field" *ngIf="m.approximateStart === 'specific_date'">
              <mat-form-field appearance="outline">
                <input matInput [matDatepicker]="picker" [value]="getStartDate(m)"
                  (dateChange)="onStartDateChange(m, $event.value)"
                  [placeholder]="'doctorReport.medsAndSupplements.datePlaceholder' | translate">
                <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                <mat-datepicker #picker></mat-datepicker>
              </mat-form-field>
            </div>

            <div class="field-group">
              <label class="label">{{ 'doctorReport.medsAndSupplements.reasonLabel' | translate }}
                <span class="important-hint">{{ 'doctorReport.medsAndSupplements.reasonHint' | translate }}</span>
              </label>
              <input class="input" [(ngModel)]="m.reason"
                [placeholder]="'doctorReport.medsAndSupplements.reasonPlaceholder' | translate" />
            </div>

            <div class="field-group">
              <label class="label">{{ 'doctorReport.medsAndSupplements.sideEffectsLabel' | translate }}
                <span class="optional">{{ 'doctorReport.optional' | translate }}</span>
              </label>
              <textarea class="textarea" [(ngModel)]="m.sideEffects"
                [placeholder]="'doctorReport.medsAndSupplements.sideEffectsPlaceholder' | translate"></textarea>
            </div>

            <button class="save-to-report-btn" (click)="toggleEditMedication(i)">
              {{ 'doctorReport.actions.saveToReport' | translate }}
            </button>
          </div>
        </ng-container>

        <button class="add-btn" (click)="addMedication()">
          {{ 'doctorReport.medsAndSupplements.addAnother' | translate }}
        </button>
      </div>

      <!-- Its own question, not a medication row: what she took BEFORE. The two
           follow-ups only open on "yes", so a no costs her a single tap. -->
      <div class="sub-block">
        <h3>{{ 'doctorReport.priorHormone.title' | translate }}</h3>

        <div class="yn-row">
          <div class="yn-text">
            {{ 'doctorReport.priorHormone.q' | translate }}
            <small>{{ 'doctorReport.priorHormone.hint' | translate }}</small>
          </div>
          <div class="yn-buttons" role="radiogroup">
            <button type="button" role="radio"
              [attr.aria-checked]="priorHormoneTherapy.tried === true"
              [class.sel]="priorHormoneTherapy.tried === true"
              (click)="setPriorHormoneTried(true)">
              {{ 'common.buttons.yes' | translate }}
            </button>
            <button type="button" role="radio"
              [attr.aria-checked]="priorHormoneTherapy.tried === false"
              [class.sel]="priorHormoneTherapy.tried === false"
              (click)="setPriorHormoneTried(false)">
              {{ 'common.buttons.no' | translate }}
            </button>
          </div>
        </div>

        <div class="conditional-area" *ngIf="priorHormoneTherapy.tried === true">
          <label class="label">{{ 'doctorReport.priorHormone.typesLabel' | translate }}
            <span class="optional">{{ 'doctorReport.priorHormone.typesHint' | translate }}</span>
          </label>
          <div class="option-list">
            <div *ngFor="let type of priorHormoneTypes" class="option-row"
              [class.selected]="isPriorHormoneType(type)" (click)="togglePriorHormoneType(type)">
              <span>{{ 'doctorReport.priorHormone.type_' + type | translate }}</span>
              <div class="checkbox-icon"></div>
            </div>
          </div>

          <label class="label">{{ 'doctorReport.priorHormone.stopLabel' | translate }}</label>
          <textarea class="textarea" [(ngModel)]="priorHormoneTherapy.stopReason"
            [placeholder]="'doctorReport.priorHormone.stopPlaceholder' | translate"
            (ngModelChange)="triggerAutoSave()"></textarea>
        </div>
      </div>

      <!-- Something she knows about and chose to leave untreated. -->
      <div class="sub-block">
        <h3>{{ 'doctorReport.untreated.title' | translate }}</h3>

        <div class="yn-row">
          <div class="yn-text">
            {{ 'doctorReport.untreated.q' | translate }}
            <small>{{ 'doctorReport.untreated.hint' | translate }}</small>
          </div>
          <div class="yn-buttons" role="radiogroup">
            <button type="button" role="radio"
              [attr.aria-checked]="untreatedCondition.has === true"
              [class.sel]="untreatedCondition.has === true"
              (click)="setUntreatedCondition(true)">
              {{ 'common.buttons.yes' | translate }}
            </button>
            <button type="button" role="radio"
              [attr.aria-checked]="untreatedCondition.has === false"
              [class.sel]="untreatedCondition.has === false"
              (click)="setUntreatedCondition(false)">
              {{ 'common.buttons.no' | translate }}
            </button>
          </div>
        </div>

        <div class="conditional-area" *ngIf="untreatedCondition.has === true">
          <label class="label">{{ 'doctorReport.untreated.detailLabel' | translate }}</label>
          <textarea class="textarea" [(ngModel)]="untreatedCondition.detail"
            [placeholder]="'doctorReport.untreated.detailPlaceholder' | translate"
            (ngModelChange)="triggerAutoSave()"></textarea>
        </div>
      </div>
    </section>

    <!-- Step 2: Cycle Trends — Bleeding pattern changes -->
    <section class="card">
      <span class="small-label">{{ 'doctorReport.step2Label' | translate }}</span>
      <h2>
        {{ (showBleedingPatterns
              ? 'doctorReport.bleeding.title'
              : 'doctorReport.bleeding.titleStopped') | translate }}
      </h2>

      <!-- One timeframe for the whole step, said once, at the top. -->
      <p class="timeframe-note" *ngIf="showBleedingPatterns">
        {{ 'doctorReport.bleeding.timeframeTop' | translate }}
      </p>

      <div class="instruction-tag" *ngIf="showBleedingPatterns">
        {{ 'doctorReport.bleeding.selectAllThatApply' | translate }}
      </div>

      <div class="option-list" *ngIf="showBleedingPatterns">
        <div *ngFor="let option of bleedingOptions" class="option-row"
          [class.selected]="isBleedingSelected(option)" (click)="toggleBleedingPattern(option)">
          <span>{{ 'doctorReport.bleeding.' + option | translate }}</span>
          <div class="checkbox-icon"></div>
        </div>

        <div class="option-row" [class.selected]="isBleedingOtherSelected"
          (click)="toggleBleedingPattern('other')">
          <span>{{ 'doctorReport.bleeding.other' | translate }}</span>
          <div class="checkbox-icon"></div>
        </div>
        <div class="conditional-area" *ngIf="isBleedingOtherSelected">
          <textarea class="textarea" [(ngModel)]="bleedingNotes"
            [placeholder]="'doctorReport.bleeding.otherPlaceholder' | translate"></textarea>
        </div>

        <!-- Exclusive, so it is set apart from the list instead of sitting in
             it looking like one more thing she can add. -->
        <div class="none-divider"></div>
        <div class="option-row none-row" [class.selected]="isNoneOfTheseSelected"
          (click)="toggleBleedingPattern('no_change')">
          <span>{{ 'doctorReport.bleeding.noneOfThese' | translate }}</span>
          <div class="checkbox-icon"></div>
        </div>
      </div>

      <!-- The list above asks what changed. These ask what actually happened.
           Sits below the list because the branch depends on "periods stopped",
           and waits for her first answer so the step does not open at full
           height with everything at once. -->
      <div class="sub-block" *ngIf="showBleedingDetail"
        [class.solo]="!showBleedingPatterns">
        <div class="why-tag" *ngIf="periodsStopped">
          {{ (knownPeriodsStopped
                ? 'doctorReport.bleeding.detail.whyFromMapping'
                : 'doctorReport.bleeding.detail.whyStopped') | translate }}
        </div>
        <h3 *ngIf="showBleedingPatterns">
          {{ (periodsStopped
                ? 'doctorReport.bleeding.detail.titleStopped'
                : 'doctorReport.bleeding.detail.titleHappened') | translate }}
        </h3>

        <div class="yn-row" *ngFor="let key of bleedingFlagRows">
          <div class="yn-text">
            {{ 'doctorReport.bleeding.detail.' + key + '.q' | translate }}
            <small>{{ 'doctorReport.bleeding.detail.' + key + '.hint' | translate }}</small>
          </div>
          <div class="yn-buttons" role="radiogroup">
            <button type="button" role="radio"
              [attr.aria-checked]="bleedingFlag(key) === true"
              [class.sel]="bleedingFlag(key) === true"
              (click)="setBleedingFlag(key, true)">
              {{ 'common.buttons.yes' | translate }}
            </button>
            <button type="button" role="radio"
              [attr.aria-checked]="bleedingFlag(key) === false"
              [class.sel]="bleedingFlag(key) === false"
              (click)="setBleedingFlag(key, false)">
              {{ 'common.buttons.no' | translate }}
            </button>
          </div>
        </div>

        <div class="route-note">{{ 'doctorReport.bleeding.detail.route' | translate }}</div>
      </div>
    </section>

    <!-- Step 3: Lifestyle - her everyday pattern.
         Every question asks a FREQUENCY, never an identity: "how many of your
         meals are prepared at home" instead of "do you cook or order in", which
         reads as a judgement and gets answered with whatever sounds right. A
         frequency can be measured and compared over time; an identity cannot.
         Nothing here is scored, ranked, or called healthy versus unhealthy. -->
    <section class="card">
      <span class="small-label">{{ 'doctorReport.step3Label' | translate }}</span>
      <h2>{{ 'doctorReport.lifestyle.title' | translate }}</h2>
      <div class="sub-note">{{ 'doctorReport.lifestyle.sub' | translate }}</div>

      <!-- Alcohol: how often, not whether. A yes/no here tells a clinician
           almost nothing and invites the answer that sounds right. -->
      <div class="scale-row">
        <div class="scale-q">{{ 'doctorReport.lifestyle.alcohol.q' | translate }}</div>
        <div class="chips" role="radiogroup"
          [attr.aria-label]="'doctorReport.lifestyle.alcohol.q' | translate">
          <button type="button" role="radio" *ngFor="let o of alcoholOptions"
            [attr.aria-checked]="isLifestyle('alcohol', o)"
            [class.sel]="isLifestyle('alcohol', o)"
            (click)="setLifestyle('alcohol', o)">
            {{ 'doctorReport.lifestyle.alcohol.' + o | translate }}
          </button>
        </div>
      </div>

      <div class="scale-row">
        <div class="scale-q">{{ 'doctorReport.lifestyle.caffeine.q' | translate }}
          <small>{{ 'doctorReport.lifestyle.caffeine.hint' | translate }}</small>
        </div>
        <div class="chips" role="radiogroup"
          [attr.aria-label]="'doctorReport.lifestyle.caffeine.q' | translate">
          <button type="button" role="radio" *ngFor="let o of caffeineOptions"
            [attr.aria-checked]="isLifestyle('caffeine', o)"
            [class.sel]="isLifestyle('caffeine', o)"
            (click)="setLifestyle('caffeine', o)">
            {{ 'doctorReport.lifestyle.caffeine.' + o | translate }}
          </button>
        </div>
      </div>

      <!-- How often first: it is the one answer that exists even when nothing
           she does has a name. The rest folds away on "not at the moment". -->
      <div class="scale-row">
        <div class="scale-q">{{ 'doctorReport.lifestyle.exercise.q' | translate }}</div>
        <div class="chips" role="radiogroup"
          [attr.aria-label]="'doctorReport.lifestyle.exercise.q' | translate">
          <button type="button" role="radio" *ngFor="let o of exerciseWeeklyOptions"
            [attr.aria-checked]="isLifestyle('exerciseWeekly', o)"
            [class.sel]="isLifestyle('exerciseWeekly', o)"
            (click)="setLifestyle('exerciseWeekly', o)">
            {{ 'doctorReport.lifestyle.exercise.' + o | translate }}
          </button>
        </div>
      </div>

      <ng-container *ngIf="showExerciseDetail">
        <div class="scale-row">
          <div class="scale-q">{{ 'doctorReport.lifestyle.exercise.typesQ' | translate }}
            <small>{{ 'doctorReport.lifestyle.exercise.typesHint' | translate }}</small>
          </div>
          <div class="chips">
            <button type="button" *ngFor="let t of exerciseTypeOptions"
              [attr.aria-pressed]="isExerciseType(t)"
              [class.sel]="isExerciseType(t)"
              (click)="toggleExerciseType(t)">
              {{ 'doctorReport.lifestyle.exercise.type_' + t | translate }}
            </button>
          </div>
        </div>

        <div class="conditional-area" *ngIf="isExerciseType('other')">
          <input class="input" [(ngModel)]="lifestyle.exerciseOther"
            [placeholder]="'doctorReport.lifestyle.exercise.otherPlaceholder' | translate"
            (ngModelChange)="triggerAutoSave()" />
        </div>

        <div class="scale-row">
          <div class="scale-q">{{ 'doctorReport.lifestyle.exercise.changeQ' | translate }}</div>
          <div class="chips" role="radiogroup"
            [attr.aria-label]="'doctorReport.lifestyle.exercise.changeQ' | translate">
            <button type="button" role="radio" *ngFor="let o of changeOptions"
              [attr.aria-checked]="isLifestyle('exerciseChange', o)"
              [class.sel]="isLifestyle('exerciseChange', o)"
              (click)="setLifestyle('exerciseChange', o)">
              {{ 'doctorReport.lifestyle.exercise.change_' + o | translate }}
            </button>
          </div>
        </div>

        <!-- The reason in her own words is what turns "3x weekly" into
             "3x weekly, reduced since joint pain returned". -->
        <div class="conditional-area" *ngIf="showExerciseNote">
          <input class="input" [(ngModel)]="lifestyle.exerciseNote"
            [placeholder]="'doctorReport.lifestyle.exercise.notePlaceholder' | translate"
            (ngModelChange)="triggerAutoSave()" />
        </div>
      </ng-container>

      <div class="scale-row">
        <div class="scale-q">{{ 'doctorReport.lifestyle.sleep.hoursQ' | translate }}</div>
        <div class="chips" role="radiogroup"
          [attr.aria-label]="'doctorReport.lifestyle.sleep.hoursQ' | translate">
          <button type="button" role="radio" *ngFor="let o of sleepHoursOptions"
            [attr.aria-checked]="isLifestyle('sleepHours', o)"
            [class.sel]="isLifestyle('sleepHours', o)"
            (click)="setLifestyle('sleepHours', o)">
            {{ 'doctorReport.lifestyle.sleep.hours_' + o | translate }}
          </button>
        </div>
      </div>

      <!-- Hours alone miss the thing she actually reports: the waking. -->
      <div class="scale-row">
        <div class="scale-q">{{ 'doctorReport.lifestyle.sleep.wakingQ' | translate }}</div>
        <div class="chips" role="radiogroup"
          [attr.aria-label]="'doctorReport.lifestyle.sleep.wakingQ' | translate">
          <button type="button" role="radio" *ngFor="let o of sleepWakingOptions"
            [attr.aria-checked]="isLifestyle('sleepWaking', o)"
            [class.sel]="isLifestyle('sleepWaking', o)"
            (click)="setLifestyle('sleepWaking', o)">
            {{ 'doctorReport.lifestyle.sleep.waking_' + o | translate }}
          </button>
        </div>
      </div>

      <div class="scale-row">
        <div class="scale-q">{{ 'doctorReport.lifestyle.sleep.changeQ' | translate }}</div>
        <div class="chips" role="radiogroup"
          [attr.aria-label]="'doctorReport.lifestyle.sleep.changeQ' | translate">
          <button type="button" role="radio" *ngFor="let o of sleepChangeOptions"
            [attr.aria-checked]="isLifestyle('sleepChange', o)"
            [class.sel]="isLifestyle('sleepChange', o)"
            (click)="setLifestyle('sleepChange', o)">
            {{ 'doctorReport.lifestyle.sleep.change_' + o | translate }}
          </button>
        </div>
      </div>

      <div class="conditional-area" *ngIf="showSleepNote">
        <input class="input" [(ngModel)]="lifestyle.sleepNote"
          [placeholder]="'doctorReport.lifestyle.sleep.notePlaceholder' | translate"
          (ngModelChange)="triggerAutoSave()" />
      </div>

      <div class="scale-row">
        <div class="scale-q">{{ 'doctorReport.lifestyle.stress.q' | translate }}</div>
        <div class="chips" role="radiogroup"
          [attr.aria-label]="'doctorReport.lifestyle.stress.q' | translate">
          <button type="button" role="radio" *ngFor="let o of stressOptions"
            [attr.aria-checked]="isLifestyle('stress', o)"
            [class.sel]="isLifestyle('stress', o)"
            (click)="setLifestyle('stress', o)">
            {{ 'doctorReport.lifestyle.stress.' + o | translate }}
          </button>
        </div>
      </div>

      <!-- A level, not a yes/no: "former, quit 2015" is a different fact from
           "never", and both are different from a current ten a day. -->
      <div class="scale-row">
        <div class="scale-q">{{ 'doctorReport.lifestyle.smoking.q' | translate }}</div>
        <div class="chips" role="radiogroup"
          [attr.aria-label]="'doctorReport.lifestyle.smoking.q' | translate">
          <button type="button" role="radio" *ngFor="let o of smokingOptions"
            [attr.aria-checked]="isLifestyle('smoking', o)"
            [class.sel]="isLifestyle('smoking', o)"
            (click)="setLifestyle('smoking', o)">
            {{ 'doctorReport.lifestyle.smoking.' + o | translate }}
          </button>
        </div>
      </div>

      <div class="conditional-area" *ngIf="lifestyle.smoking === 'former'">
        <input class="input" [(ngModel)]="lifestyle.smokingQuitYear"
          [placeholder]="'doctorReport.lifestyle.smoking.quitPlaceholder' | translate"
          (ngModelChange)="triggerAutoSave()" />
      </div>

      <!-- Food asked as a count, never as who she is. "Do you cook at home or
           order in" sorts women into two kinds of person and gets answered with
           the flattering one; a share of her weekly meals can be measured,
           compared with her own last answer, and carries no verdict. -->
      <div class="scale-row">
        <div class="scale-q">{{ 'doctorReport.lifestyle.meals.homeQ' | translate }}</div>
        <div class="chips" role="radiogroup"
          [attr.aria-label]="'doctorReport.lifestyle.meals.homeQ' | translate">
          <button type="button" role="radio" *ngFor="let o of mealsHomeOptions"
            [attr.aria-checked]="isLifestyle('mealsHome', o)"
            [class.sel]="isLifestyle('mealsHome', o)"
            (click)="setLifestyle('mealsHome', o)">
            {{ 'doctorReport.lifestyle.meals.home_' + o | translate }}
          </button>
        </div>
      </div>

      <div class="scale-row">
        <div class="scale-q">{{ 'doctorReport.lifestyle.meals.hotQ' | translate }}</div>
        <div class="chips" role="radiogroup"
          [attr.aria-label]="'doctorReport.lifestyle.meals.hotQ' | translate">
          <button type="button" role="radio" *ngFor="let o of hotMealsOptions"
            [attr.aria-checked]="isLifestyle('hotMeals', o)"
            [class.sel]="isLifestyle('hotMeals', o)"
            (click)="setLifestyle('hotMeals', o)">
            {{ 'doctorReport.lifestyle.meals.hot_' + o | translate }}
          </button>
        </div>
      </div>

      <div class="route-note">{{ 'doctorReport.lifestyle.note' | translate }}</div>
    </section>

    <!-- Step 4: Visit Goal -->
    <section class="card">
      <span class="small-label">{{ 'doctorReport.step4Label' | translate }}</span>
      <h2>{{ 'doctorReport.visitGoal.title' | translate }}</h2>
      <textarea class="textarea" [(ngModel)]="visitGoal"
        [placeholder]="'doctorReport.visitGoal.placeholder' | translate"
        (ngModelChange)="triggerAutoSave()"></textarea>

      <!-- The open question stays first on purpose: a list of her symptoms
           above it would anchor her answer and cost us her own words. -->
      <div class="sub-block">
        <h3>{{ 'doctorReport.visitGoal.improveTitle' | translate }}</h3>
        <!-- Silent until the request settles: saying "nothing rated yet" while
             the answer is still in flight states the opposite of the truth on
             every scan she DID rate. -->
        <div class="sub-note" *ngIf="ratedLoaded">
          {{ (ratedSymptoms.length ? 'doctorReport.visitGoal.improveSub'
                                   : 'doctorReport.visitGoal.improveEmpty') | translate }}
        </div>

        <!-- Nothing rated yet: the heading still stands so the step reads whole,
             but there is no slot to offer and no list to pick from. -->
        <div class="slots" *ngIf="ratedSymptoms.length">
          <ng-container *ngFor="let key of concerns; let i = index">
            <div class="slot" [class.filled]="!!key">
              <span class="slot-num">{{ i + 1 }}</span>
              <span class="slot-name">
                {{ key ? (concernLabelKey(key) | translate)
                       : ('doctorReport.visitGoal.emptySlot' | translate) }}
              </span>
              <span class="slot-score" *ngIf="key && concernRating(key) !== null">
                {{ concernRating(key) }}
              </span>
              <button type="button" class="slot-action" (click)="toggleConcernPicker(i)">
                {{ (key ? 'doctorReport.visitGoal.swap'
                        : 'doctorReport.visitGoal.choose') | translate }}
              </button>
            </div>

            <div class="picker" *ngIf="concernPickerIndex === i">
              <div class="picker-head">{{ 'doctorReport.visitGoal.pickerTitle' | translate }}</div>
              <button type="button" class="picker-row" *ngFor="let option of concernOptions()"
                (click)="pickConcern(i, option.key)">
                <span>{{ concernLabelKey(option.key) | translate }}</span>
                <span class="picker-score">{{ option.value }}</span>
              </button>
              <button type="button" class="picker-cancel" (click)="toggleConcernPicker(i)">
                {{ 'doctorReport.visitGoal.pickerCancel' | translate }}
              </button>
            </div>
          </ng-container>
        </div>
      </div>
    </section>
  </main>

  <div class="cta-container">
    <button class="primary-cta" (click)="generateReport()" [disabled]="saving">
      {{ 'doctorReport.actions.generateAndShare' | translate }}
    </button>
  </div>
</div>
